/**
 * Vuex module for the Listing Search feature.
 *
 * Holds the criteria the user is composing in the form, the in-flight search
 * state (search_id + per-source progress), and the polling timer that streams
 * results from the backend cache.
 */
export default {
    state: {
        criteria: {
            property_type: 'office',
            city: '',
            state: '',
            min_sf: null,
            max_sf: null,
            brokerage_ids: [],
            include_firecrawl_open: true,
        },

        searchId: null,

        // The cached payload returned by GET /api/listing-search/{id}.
        // Shape: { status, criteria, sources: { key: {column, name, status, listings, ...} } }
        searchState: null,

        isDispatching: false,
        pollTimer: null,
        errorMessage: null,
    },

    mutations: {
        SET_LISTING_CRITERIA(state, partial) {
            state.criteria = { ...state.criteria, ...partial };
        },

        RESET_LISTING_SEARCH(state) {
            if (state.pollTimer) {
                clearInterval(state.pollTimer);
            }
            state.pollTimer = null;
            state.searchId = null;
            state.searchState = null;
            state.errorMessage = null;
        },

        SET_LISTING_DISPATCHING(state, value) {
            state.isDispatching = value;
        },

        SET_LISTING_SEARCH_ID(state, id) {
            state.searchId = id;
        },

        SET_LISTING_SEARCH_STATE(state, payload) {
            state.searchState = payload;
        },

        SET_LISTING_POLL_TIMER(state, timer) {
            state.pollTimer = timer;
        },

        SET_LISTING_ERROR(state, message) {
            state.errorMessage = message;
        },
    },

    actions: {
        async runListingSearch({ state, commit, dispatch }) {
            commit('RESET_LISTING_SEARCH');
            commit('SET_LISTING_DISPATCHING', true);

            try {
                const { data } = await axios.post('/api/listing-search', state.criteria);
                if (!data || !data.success) {
                    throw new Error(data?.message || 'Search failed to dispatch.');
                }
                commit('SET_LISTING_SEARCH_ID', data.data.search_id);
                dispatch('startListingPolling');
            } catch (e) {
                commit('SET_LISTING_ERROR', e?.response?.data?.message || e.message);
            } finally {
                commit('SET_LISTING_DISPATCHING', false);
            }
        },

        startListingPolling({ state, commit, dispatch }) {
            if (state.pollTimer) {
                clearInterval(state.pollTimer);
            }
            // Immediate first poll so the UI flips out of "dispatching" without
            // waiting a full interval.
            dispatch('pollListingSearch');
            const timer = setInterval(() => dispatch('pollListingSearch'), 2000);
            commit('SET_LISTING_POLL_TIMER', timer);
        },

        async pollListingSearch({ state, commit }) {
            if (!state.searchId) return;
            try {
                const { data } = await axios.get(`/api/listing-search/${state.searchId}`);
                if (!data?.success) return;

                commit('SET_LISTING_SEARCH_STATE', data.data);

                const terminal = ['done', 'failed'].includes(data.data?.status);
                if (terminal && state.pollTimer) {
                    clearInterval(state.pollTimer);
                    commit('SET_LISTING_POLL_TIMER', null);
                }
            } catch (e) {
                // Swallow transient polling errors; user can retry.
                console.warn('Listing search poll failed', e);
            }
        },

        stopListingPolling({ state, commit }) {
            if (state.pollTimer) {
                clearInterval(state.pollTimer);
                commit('SET_LISTING_POLL_TIMER', null);
            }
        },
    },

    getters: {
        listingBrokerSources(state) {
            const sources = state.searchState?.sources || {};
            return Object.values(sources).filter((s) => s.column === 'brokers');
        },

        listingFirecrawlSources(state) {
            const sources = state.searchState?.sources || {};
            return Object.values(sources).filter((s) => s.column === 'firecrawl');
        },

        listingSearchInProgress(state) {
            const status = state.searchState?.status;
            return state.isDispatching || status === 'pending' || status === 'running';
        },
    },
};
